import type { MessageApi } from '@/features/messages/api/message-api'
import type { JobsApi } from '../api/job-api'
import { Id } from '@/infra/id'
import {
  DownloadConsultationCalendarRequest,
  SetConsultationAvailabilityRequest,
} from '@taxfyle/api-internal/internal/consultation_pb'
import type { TimeRange } from '@/infra/time-range'
import type { GetCurrentUserFn } from '@/features/iam'

export class ConsultationStore {
  constructor(
    private readonly _jobsApi: JobsApi,
    private readonly _messageApi: MessageApi,
    private readonly _getCurrentUser: GetCurrentUserFn,
  ) {}

  /**
   * Sets the availability for a consultation.
   * @param jobId
   * @param availability
   */
  async setConsultationAvailability(jobId: Id, availability: TimeRange[]) {
    await this._jobsApi.consultations.setConsultationAvailability(
      new SetConsultationAvailabilityRequest()
        .setJobId(Id.assert(jobId))
        .setAvailabilityList(
          availability.map((range) => range.toProtoInterval()),
        ),
    )
  }

  /**
   * Joins a room.
   * @param roomId
   */
  async joinRoom(roomId: string) {
    const room = await this._messageApi.rooms.join(roomId)

    if (room.type === 'Whereby') {
      const url = new URL(room.room_url)
      // Set the display name to the current user's name so they don't have to
      // enter it.
      url.searchParams.set('displayName', this._getCurrentUser().displayName)
      return url
    }

    return null
  }

  /**
   * Fetches the calendar for a consultation.
   * @param jobId
   */
  async fetchConsultationCalendar(jobId: Id) {
    return await this._jobsApi.consultations
      .downloadConsultationCalendar(
        new DownloadConsultationCalendarRequest().setId(Id.assert(jobId)),
      )
      .then((resp) => {
        const data = resp.getData_asU8()
        const arrayBuffer = new ArrayBuffer(data.length)
        const view = new Uint8Array(arrayBuffer)
        view.set(data)
        return new File([arrayBuffer], 'calendar.ics', {
          type: 'text/calendar',
        })
      })
  }
}
